import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import type { Counts, UserBrief } from '../../types'
import { likePost, unlikePost, savePost, unsavePost, sharePost, sendPostPixeCardEmail } from '../../api/posts'
import { getGiftCatalog } from '../../api/gifts'
import GiftToast from '../gifts/GiftToast'
import { buildPixeCardShareUrl } from '../../lib/pixecardShare'

type MobileActionBarProps = {
  postId: number
  counts: Counts
  myState?: { liked: boolean; saved: boolean }
  user: UserBrief
  shareUrl?: string | null
  /** Optional: invoked instead of default navigation to post for comments. */
  onOpenComments?: () => void
  /** Optional: invoked instead of fallback gift behavior. */
  onGift?: () => void
  /** Optional: invoked when user taps the share button (after copy/share). */
  onShared?: () => void
}

export default function MobileActionBar({ postId, counts, myState, user, shareUrl, onOpenComments, onGift, onShared }: MobileActionBarProps) {
  const navigate = useNavigate()
  const [liked, setLiked] = useState<boolean>(!!myState?.liked)
  const [saved, setSaved] = useState<boolean>(!!myState?.saved)
  const [likes, setLikes] = useState<number>(counts?.likes ?? 0)
  const [saves, setSaves] = useState<number>(counts?.saves ?? 0)
  const [shares, setShares] = useState<number>(counts?.shares ?? 0)
  const [emailModalOpen, setEmailModalOpen] = useState(false)
  const [emailTo, setEmailTo] = useState('')
  const [emailBusy, setEmailBusy] = useState(false)
  const [emailError, setEmailError] = useState<string | null>(null)
  const [toast, setToast] = useState<{ visible: boolean; label: string; detail?: string; kind?: 'success' | 'error' }>({
    visible: false,
    label: '',
    detail: '',
    kind: 'success',
  })
  const comments = counts?.comments ?? 0

  useEffect(() => { setLiked(!!myState?.liked) }, [myState?.liked])
  useEffect(() => { setSaved(!!myState?.saved) }, [myState?.saved])
  useEffect(() => { if (typeof counts?.likes === 'number') setLikes(counts.likes) }, [counts?.likes])
  useEffect(() => { if (typeof counts?.saves === 'number') setSaves(counts.saves) }, [counts?.saves])
  useEffect(() => { if (typeof counts?.shares === 'number') setShares(counts.shares) }, [counts?.shares])

  const authGuard = useCallback((fn: () => Promise<void>) => {
    return fn().catch((err: any) => {
      const status = err?.response?.status
      if (status === 401) navigate('/login')
      // eslint-disable-next-line no-console
      else console.warn('Action failed', err)
    })
  }, [navigate])

  const toggleLike = useCallback(() => {
    const next = !liked
    setLiked(next)
    setLikes((c) => Math.max(0, c + (next ? 1 : -1)))
    authGuard(async () => {
      if (next) await likePost(postId)
      else await unlikePost(postId)
    })
  }, [authGuard, liked, postId])

  const toggleSave = useCallback(() => {
    const next = !saved
    setSaved(next)
    setSaves((c) => Math.max(0, c + (next ? 1 : -1)))
    authGuard(async () => {
      if (next) await savePost(postId)
      else await unsavePost(postId)
    })
  }, [authGuard, postId, saved])

  const doShare = useCallback(async () => {
    const url = buildPixeCardShareUrl(postId, shareUrl)
    const title = 'Check this out on Pixeloop'
    try {
      if ((navigator as any).share) {
        await (navigator as any).share({ url, title })
      } else {
        await navigator.clipboard.writeText(url)
      }
      setShares((c) => c + 1)
      await sharePost(postId)
      onShared?.()
    } catch {}
  }, [onShared, postId, shareUrl])

  const openEmailModal = useCallback(() => {
    setEmailError(null)
    setEmailModalOpen(true)
  }, [])

  const closeEmailModal = useCallback(() => {
    if (emailBusy) return
    setEmailModalOpen(false)
    setEmailError(null)
  }, [emailBusy])

  const sendEmailShare = useCallback(async () => {
    const email = emailTo.trim().toLowerCase()
    if (!email) {
      setEmailError('Email is required.')
      return
    }
    setEmailBusy(true)
    setEmailError(null)
    try {
      await sendPostPixeCardEmail({ postId, email })
      await sharePost(postId)
      setShares((c) => c + 1)
      onShared?.()
      setEmailModalOpen(false)
      setToast({ visible: true, label: 'PixeCard sent', detail: email, kind: 'success' })
    } catch (err: any) {
      const status = Number(err?.response?.status || 0)
      if (status === 401) {
        navigate('/login')
        return
      }
      setEmailError(err?.response?.data?.detail || err?.message || 'Could not send email right now.')
    } finally {
      setEmailBusy(false)
    }
  }, [emailTo, navigate, onShared, postId])

  const prefetchGifts = useCallback(() => { getGiftCatalog().catch(() => null) }, [])
  const handleGiftClick = useCallback(() => {
    prefetchGifts()
    if (onGift) return onGift()
    alert('Gifts: open this post to send a gift.')
  }, [onGift, prefetchGifts])

  const openComments = useCallback(() => {
    if (onOpenComments) return onOpenComments()
    // Default: open the post detail view (comments live there today).
    navigate(`/p/${postId}`)
  }, [navigate, onOpenComments, postId])

  const goToCreator = useCallback(() => {
    if (user?.profile_public_id) navigate(`/people/i/${user.profile_public_id}`)
  }, [navigate, user?.profile_public_id])

  const ActionBtn = useMemo(() =>
    function ActionBtn({ onClick, label, children }: { onClick?: () => void; label: string; children: React.ReactNode }) {
      return (
        <button
          type="button"
          onPointerDown={(e) => e.stopPropagation()}
          onPointerUp={(e) => e.stopPropagation()}
          onClick={(e) => { e.stopPropagation(); onClick?.() }}
          aria-label={label}
          className="pointer-events-auto h-11 w-11 rounded-full border border-white/10 bg-black/35 backdrop-blur hover:bg-black/45 flex items-center justify-center"
        >
          {children}
        </button>
      )
    }
  , [])

  const Count = ({ n }: { n: number }) => (
    <div className="mt-1 text-[11px] text-white/85 drop-shadow">{n > 9999 ? `${Math.round(n / 1000)}k` : n}</div>
  )

  return (
    <div className="pointer-events-none absolute right-3 bottom-[calc(1rem+env(safe-area-inset-bottom))] flex flex-col items-center gap-4">
      {/* Avatar moved to the top of the stack */}
      {user?.profile_public_id ? (
        <button
          type="button"
          onPointerDown={(e) => e.stopPropagation()}
          onPointerUp={(e) => e.stopPropagation()}
          onClick={(e) => { e.stopPropagation(); goToCreator() }}
          aria-label="Open creator profile"
          className="pointer-events-auto h-11 w-11 rounded-full overflow-hidden border border-white/25 bg-black/35 backdrop-blur"
        >
          {user?.avatar ? <img src={user.avatar} className="h-full w-full object-cover" /> : <div className="h-full w-full bg-white/10" />}
        </button>
      ) : null}

      {/* Heart must be above other actions */}
      <div className="flex flex-col items-center">
        <ActionBtn onClick={toggleLike} label={liked ? 'Unlike' : 'Like'}>
          <i className={`fa-solid fa-heart ${liked ? 'text-red-500' : 'text-white'}`} style={{ fontSize: 22 }} />
        </ActionBtn>
        <Count n={likes} />
      </div>

      <div className="flex flex-col items-center">
        <ActionBtn onClick={openComments} label="Comments">
          <i className="fa-solid fa-comment text-white" style={{ fontSize: 22 }} />
        </ActionBtn>
        <Count n={comments} />
      </div>

      <div className="flex flex-col items-center">
        <ActionBtn onClick={toggleSave} label={saved ? 'Unsave' : 'Save'}>
          <i className={`fa-solid fa-floppy-disk ${saved ? 'text-sky-300' : 'text-white'}`} style={{ fontSize: 20 }} />
        </ActionBtn>
        <Count n={saves} />
      </div>

      <div className="relative flex flex-col items-center">
        <ActionBtn onClick={openEmailModal} label="Share">
          <i className="fa-solid fa-share text-white" style={{ fontSize: 20 }} />
        </ActionBtn>
        <Count n={shares} />
      </div>

      {/* Gift now occupies the previous bottom-most slot */}
      <div className="flex flex-col items-center" onMouseEnter={prefetchGifts} onPointerDown={(e) => e.stopPropagation()} onPointerUp={(e) => e.stopPropagation()} onClick={(e) => e.stopPropagation()}>
        <ActionBtn onClick={handleGiftClick} label="Send gift">
          <i className="fa-solid fa-gift" style={{ fontSize: 22, color: '#b222c5' }} />
        </ActionBtn>
      </div>
      {emailModalOpen ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <button type="button" className="absolute inset-0 bg-black/70" onClick={closeEmailModal} aria-label="Close email share dialog" />
          <div className="relative w-full max-w-md rounded-2xl border border-white/15 bg-slate-900 p-4">
            <div className="text-base font-semibold text-white">Send PixeCard by email</div>
            <label className="mt-3 block text-xs uppercase tracking-wide text-white/65">To</label>
            <input
              type="email"
              value={emailTo}
              onChange={(e) => setEmailTo(e.target.value)}
              placeholder="name@example.com"
              className="mt-1 w-full rounded-lg border border-white/15 bg-black/30 px-3 py-2 text-sm text-white outline-none focus:border-fuchsia-400"
            />
            {emailError ? <div className="mt-2 text-xs text-rose-300">{emailError}</div> : null}
            <button
              type="button"
              onClick={() => void doShare()}
              className="mt-2 text-xs text-white/75 underline hover:text-white"
              disabled={emailBusy}
            >
              Share link instead
            </button>
            <div className="mt-4 flex items-center justify-end gap-2">
              <button
                type="button"
                onClick={closeEmailModal}
                className="rounded-md border border-white/20 px-3 py-1.5 text-sm text-white/80 hover:bg-white/10"
                disabled={emailBusy}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => void sendEmailShare()}
                className="rounded-md bg-fuchsia-600 px-3 py-1.5 text-sm font-semibold text-white hover:bg-fuchsia-500 disabled:opacity-60"
                disabled={emailBusy}
              >
                {emailBusy ? 'Sending...' : 'Send'}
              </button>
            </div>
          </div>
        </div>
      ) : null}
      <GiftToast
        visible={toast.visible}
        emoji={toast.kind === 'error' ? '!' : 'OK'}
        label={toast.label}
        detail={toast.detail}
        kind={toast.kind}
        onHide={() => setToast((t) => ({ ...t, visible: false }))}
      />
    </div>
  )
}
